import { Listbox } from '@headlessui/react';
import * as React from 'react';
import { CaptionTypography } from '@components/atoms';
import { cn } from '@utils';

export type ListBoxOption = {
	key?: React.Key;
	label: string;
	value: string | number;
};

type OptionComponentProps = {
	option: ListBoxOption;
	isSelected?: boolean;
};

type Props = {
	options: ListBoxOption[];
	onSelect: (value: ListBoxOption['value']) => void;
	OptionComponent: React.FC<OptionComponentProps>;
	placeholder: string;
	defaultOpenOnValue?: ListBoxOption['value'];
	textCenter?: boolean;
	value?: ListBoxOption['value'] | null;
};

export const ListBox: React.FC<Props> = ({
	options,
	onSelect,
	OptionComponent,
	placeholder,
	defaultOpenOnValue,
	textCenter,
	value,
}) => {
	const isControlled = value !== undefined;
	const [uncontrolledSelected, setUncontrolledSelected] = React.useState<ListBoxOption['value'] | null>(null);
	const selected = isControlled ? value : uncontrolledSelected;

	const onChange = (nextValue: ListBoxOption['value']) => {
		onSelect(nextValue);
		if (!isControlled) {
			setUncontrolledSelected(nextValue);
		}
	};

	const selectedOption = React.useMemo(() => options.find(option => option.value === selected), [options, selected]);

	return (
		<div className="relative w-full" data-aid="select-list-box">
			<Listbox value={selected} onChange={onChange}>
				<Listbox.Button
					key={placeholder}
					className={cn(
						'w-full rounded-2xl border-2 border-borderPrimary bg-white px-4 py-2  hover:cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-gray-200',
						selectedOption ? 'text-black' : 'text-secondary',
						textCenter ? 'text-center' : 'text-start'
					)}
				>
					<CaptionTypography className="text-[1.5rem] text-primary">
						{selectedOption?.label ?? placeholder}
					</CaptionTypography>
				</Listbox.Button>
				<Listbox.Options
					ref={element => {
						if (!element) {
							return;
						}

						const scrollValue: ListBoxOption['value'] | undefined = selected ?? defaultOpenOnValue;

						if (!scrollValue) {
							return;
						}

						const child: Element | null = element.querySelector(`[data-value="${scrollValue}"]`);

						child?.scrollIntoView({ behavior: 'auto', block: 'center' });
					}}
					className="absolute left-0 z-50 mt-1 max-h-96 w-full space-y-1.5 overflow-y-auto rounded-xl border border-borderPrimary bg-white p-1.5 text-center shadow-lg hover:cursor-pointer max-sm:max-h-72"
				>
					{options.map(option => {
						const isSelected: boolean = selectedOption?.value === option.value;

						return (
							<Listbox.Option
								data-aid="select-list-box-option"
								key={option.key ?? `${option.label}_${option.value}`}
								value={option.value}
								data-value={option.value}
								className={cn(
									'pt-[1.5rem] pb-[1.5rem] hover:rounded-md hover:bg-[#F3F7FA]',
									isSelected ? 'pointer-events-none rounded-md bg-[#DAE6F0]' : ''
								)}
							>
								<OptionComponent option={option} isSelected={isSelected} />
							</Listbox.Option>
						);
					})}
				</Listbox.Options>
			</Listbox>
		</div>
	);
};
